/**
 * Pure view model for the admin audit-log page (HUB-115; read contract
 * HUB-063 §4 / AUD-02).
 *
 * The page lists single `tf_audit_event` rows: who did what, when, to which
 * record, and — for a write a track reported — the old and new value. Every
 * label and sentence the page shows is composed here, not in the component,
 * so the unit tests can pin the wording without `@/lib/hub-client` (it boots
 * a HubClient at module load and throws under vitest).
 *
 * What this module encodes:
 *  - Track-reported rows (`createTrackAudit(...).recordAuditEvent`, hub-client
 *    `next/audit`) carry `{ field, oldValue, newValue }` in `body`; that is
 *    the "old and new value" of the requirement and it renders as a diff
 *    block, not as JSON an operator has to read.
 *  - The hub stores and RETURNS the redacted body snapshot — it does not hash
 *    values away (`payload_hash` is a reproducibility hash OVER the redacted
 *    envelope, HUB-063 §2). The page may show the body as-is.
 *  - `route` is filtered by equality on the hub, so the filter is labelled as
 *    exact (owner decision 2026-10-16), never "search".
 *
 * Dependency-light on purpose: `@/lib/format`, `@/lib/list-filters` and
 * `@/lib/pagination` only.
 */
import type {
  AuditEventListQuery,
  AuditEventView,
  AuditSource,
} from '@verybigthings/trackframe-shared/schemas';
import { formatCount } from '@/lib/format';
import { isUuid } from '@/lib/list-filters';
import { pageCountOf, pageIndexOf } from '@/lib/pagination';

export type AuditSourceFilter = AuditSource | 'all';

/** Track first: it is the default and the "who did what" view (HUB-115). */
export const AUDIT_SOURCE_OPTIONS: readonly { value: AuditSourceFilter; label: string }[] = [
  { value: 'track', label: 'Track' },
  { value: 'hub_api', label: 'Hub API' },
  { value: 'extension', label: 'Extension' },
  { value: 'all', label: 'All sources' },
];

export const AUDIT_PAGE_SIZE = 50;

/** URL-state defaults (`useUrlState`): module-level so the setter is stable. */
export const AUDIT_FILTER_DEFAULTS = {
  source: 'track',
  trackId: '',
  actorUserId: '',
  route: '',
  from: '',
  to: '',
  page: '1',
};
export type AuditFilters = typeof AUDIT_FILTER_DEFAULTS;

export const AUDIT_BODY_TRUNCATED_COPY = 'Snapshot truncated at 16 KB — the stored body is incomplete.';

export const AUDIT_DENIED_COPY = 'You do not have access to the audit log.';

export const AUDIT_ERROR_PREFIX = 'Could not load audit events';

const SOURCE_VALUES: ReadonlySet<string> = new Set(AUDIT_SOURCE_OPTIONS.map((o) => o.value));

/** The source filter the URL asked for, or the default when it names no known
 *  source — a typo'd `?source=` must not become a hub 400 the user cannot
 *  clear from the page. */
export function resolveSourceFilter(value: string): AuditSourceFilter {
  return SOURCE_VALUES.has(value) ? (value as AuditSourceFilter) : (AUDIT_FILTER_DEFAULTS.source as AuditSourceFilter);
}

/** `YYYY-MM-DD` → UTC start or end of that day as ISO; `undefined` when unparseable. */
function dayBound(date: string, edge: 'start' | 'end'): string | undefined {
  if (!date) return undefined;
  const time = edge === 'start' ? 'T00:00:00.000Z' : 'T23:59:59.999Z';
  const d = new Date(`${date}${time}`);
  return Number.isNaN(d.getTime()) ? undefined : d.toISOString();
}

/**
 * Filters → the typed `GET /audit/events` query. A half-typed UUID is "not
 * filtering yet" (the route's pattern would 400 and the previous page would
 * pose as the filtered one); `'all'` sends no source.
 */
export function buildAuditQuery(filters: AuditFilters): AuditEventListQuery {
  const query: AuditEventListQuery = {
    limit: AUDIT_PAGE_SIZE,
    offset: pageIndexOf(filters.page) * AUDIT_PAGE_SIZE,
  };
  const source = resolveSourceFilter(filters.source);
  if (source !== 'all') query.source = source as AuditSource;
  if (isUuid(filters.trackId)) query.trackId = filters.trackId.trim();
  if (isUuid(filters.actorUserId)) query.actorUserId = filters.actorUserId.trim();
  const route = filters.route.trim();
  if (route) query.route = route;
  const from = dayBound(filters.from, 'start');
  if (from) query.from = from;
  const to = dayBound(filters.to, 'end');
  if (to) query.to = to;
  return query;
}

/** Email, else the verified API-key prefix, else `anonymous` (HUB-063 actor model). */
export function actorLabel(row: AuditEventView): string {
  if (row.actorEmail) return row.actorEmail;
  if (row.actorApiKeyPrefix) return `API key ${row.actorApiKeyPrefix}`;
  return 'anonymous';
}

/**
 * `POST /audit/events` defaults BOTH `httpMethod` and `httpRoute` to the
 * reported `action`, so a row whose two columns agree carries one verb and
 * showing both would read `trial.updated trial.updated`. Decided by SHAPE, not
 * by source: a track that reports a real `method` or `route` beside its action
 * (hub-client's Fastify middleware does) keeps both halves, and a hub row is
 * always `METHOD /route/pattern`.
 */
export function actionLabel(row: AuditEventView): string {
  if (row.httpMethod === row.httpRoute) return row.httpRoute;
  return `${row.httpMethod} ${row.httpRoute}`;
}

export interface AuditFieldChange {
  field: string;
  oldValue: string;
  newValue: string;
}

function changeValue(value: unknown): string {
  if (value === undefined || value === null) return '—';
  if (typeof value === 'string') return value;
  return JSON.stringify(value);
}

/**
 * The old → new block for a reported write: `body.field` names the changed
 * field (`created` / `deleted` on create and delete), the two values are
 * strings as the track sent them. Shape-based, not source-based, so an
 * extension that reports the same shape gets the same rendering — but the
 * shape is `field` PLUS at least one value side, so a request body that merely
 * has a `field` key is not shown as a change from nothing to nothing. `null`
 * for anything else — the caller falls back to the raw JSON.
 */
export function fieldChange(row: AuditEventView): AuditFieldChange | null {
  const body = row.body;
  if (typeof body !== 'object' || body === null || Array.isArray(body)) return null;
  const record = body as Record<string, unknown>;
  if (typeof record.field !== 'string') return null;
  if (!('oldValue' in record) && !('newValue' in record)) return null;
  return {
    field: record.field,
    oldValue: changeValue(record.oldValue),
    newValue: changeValue(record.newValue),
  };
}

/** The redacted snapshot, pretty-printed; `null` when the row has none. */
export function bodyText(row: AuditEventView): string | null {
  if (row.body === undefined) return null;
  return JSON.stringify(row.body, null, 2);
}

export function auditEmptyCopy(source: string): string {
  if (source === 'track') return 'No track-reported events match the current filters.';
  return 'No audit events match the current filters.';
}

/** A stale `?page=` past the end (rows aged out, or a shared link) is not a
 *  filter problem, and the empty state must not say it is. */
export function auditPageOutOfRangeCopy(total: number): string {
  const pages = pageCountOf(total, AUDIT_PAGE_SIZE);
  return `This page is past the end — ${formatCount(total)} ${total === 1 ? 'event' : 'events'} fit on ${pages} ${pages === 1 ? 'page' : 'pages'}. Go back to page 1.`;
}

export function auditTotalSentence(total: number, pageIndex: number): string {
  const noun = total === 1 ? 'event' : 'events';
  return `${formatCount(total)} ${noun} · Page ${pageIndex + 1} of ${pageCountOf(total, AUDIT_PAGE_SIZE)}`;
}

/** 403 is the authz gate answering; everything else is a load failure. */
export function auditErrorCopy(httpStatus: number | null, message: string): string {
  if (httpStatus === 403) return AUDIT_DENIED_COPY;
  return `${AUDIT_ERROR_PREFIX}: ${message}`;
}
